/**
 * PublicContentService - Service layer for managing public content and promotional data
 * 
 * Features:
 * - Masked surebet data fetching
 * - Educational content management
 * - FAQ content management
 * - Contact form submission handling
 * - Analytics tracking for public page interactions
 * - Content localization for Bulgarian/English
 */

import apiClient from '../../api/config/ApiClient';
import { API_ENDPOINTS, API_TIMEOUTS } from '../../utils/Constants';
import { LANGUAGES } from '../../utils/Constants';

class PublicContentService {
  /**
   * Cache configuration
   */
  static cache = {
    maskedData: {
      data: null,
      timestamp: null,
      ttl: 300000 // 5 minutes cache TTL for masked data
    },
    educationalContent: {
      data: null,
      timestamp: null,
      ttl: 3600000 // 1 hour cache TTL for educational content
    },
    faqContent: {
      data: null,
      timestamp: null,
      ttl: 3600000 // 1 hour cache TTL for FAQ content
    }
  };

  /**
   * Request deduplication - prevent multiple simultaneous calls to same endpoint
   */
  static pendingRequests = new Map();

  /**
   * Analytics tracking data
   */
  static analytics = {
    pageViews: new Map(),
    interactions: [],
    sessionStart: null
  };

  /**
   * Retry configuration
   */
  static retryConfig = {
    maxRetries: 3,
    retryDelay: 2000, // 2 seconds
    retryableStatusCodes: [408, 429, 500, 502, 503, 504]
  };

  /**
   * Check if cache is valid
   * @param {string} cacheKey - Cache key to check
   * @returns {boolean} True if cache is valid
   */
  static isCacheValid(cacheKey) {
    const cache = this.cache[cacheKey];
    if (!cache || !cache.data || !cache.timestamp) {
      return false;
    }
    const age = Date.now() - cache.timestamp;
    return age < cache.ttl;
  }

  /**
   * Get masked surebet data (public endpoint)
   * Delegates to SurebetService or fetches directly
   * 
   * @param {Object} options - Request options
   * @param {number} options.maxItems - Maximum number of items to return
   * @param {string} options.language - Language for localization (bg/en)
   * @returns {Promise<{data: Array|null, error: string|null}>}
   */
  static async getMaskedSurebetData(options = {}) {
    const { maxItems = 5, language = LANGUAGES.BULGARIAN } = options;
    const endpoint = '/api/core/markets-analysis/masked';
    const requestId = `masked-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Check cache first
      if (this.isCacheValid('maskedData')) {
        console.log(`📦 PublicContentService [${requestId}]: Using cached masked data`);
        return { data: this.cache.maskedData.data, error: null };
      }

      // Check if request is already in progress
      if (this.pendingRequests.has(endpoint)) {
        console.log(`⏳ PublicContentService [${requestId}]: Request already in progress, waiting...`);
        const result = await this.pendingRequests.get(endpoint);
        return result;
      }

      // Create new request promise
      const requestPromise = (async () => {
        try {
          const response = await apiClient.get(endpoint, {
            params: { maxItems, language },
            timeout: API_TIMEOUTS.DATA_REFRESH
          });

          const data = response.data || response;
          
          // Update cache
          this.cache.maskedData = {
            data: data,
            timestamp: Date.now(),
            ttl: 300000
          };

          // Track analytics
          this.trackAnalytics('masked_data_fetch', { itemsCount: Array.isArray(data) ? data.length : 0 });

          return { data: data, error: null };
        } catch (error) {
          console.error(`❌ PublicContentService [${requestId}]: Error fetching masked data:`, error);
          return { data: null, error: this.handleError(error) };
        }
      })();

      // Store the promise to prevent duplicate requests
      this.pendingRequests.set(endpoint, requestPromise);
      const result = await requestPromise;
      this.pendingRequests.delete(endpoint);

      return result;
    } catch (error) {
      console.error(`❌ PublicContentService [${requestId}]: Unexpected error:`, error);
      return { data: null, error: this.handleError(error) };
    }
  }

  /**
   * Get educational content
   * 
   * @param {string} language - Language for content (bg/en)
   * @param {string} section - Specific section to retrieve (optional)
   * @returns {Promise<{data: Object|null, error: string|null}>}
   */
  static async getEducationalContent(language = LANGUAGES.BULGARIAN, section = null) {
    const cacheKey = 'educationalContent';
    const requestId = `educational-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Check cache first
      if (this.isCacheValid(cacheKey)) {
        console.log(`📦 PublicContentService [${requestId}]: Using cached educational content`);
        const cachedData = this.cache[cacheKey].data;
        if (section && cachedData[section]) {
          return { data: { [section]: cachedData[section] }, error: null };
        }
        return { data: cachedData, error: null };
      }

      // For now, educational content is static and comes from translations
      // In the future, this could fetch from an API
      const educationalContent = {
        whatIsSurebet: {
          title: language === LANGUAGES.BULGARIAN 
            ? 'Какво е арбитражен залог (Surebet)?'
            : 'What is an arbitrage bet (Surebet)?',
          content: language === LANGUAGES.BULGARIAN
            ? 'Арбитражният залог е стратегия за безрисково залагане...'
            : 'Arbitrage betting is a risk-free betting strategy...'
        },
        // Add more sections as needed
      };

      // Update cache
      this.cache[cacheKey] = {
        data: educationalContent,
        timestamp: Date.now(),
        ttl: 3600000
      };

      // Track analytics
      this.trackAnalytics('educational_content_view', { language, section });

      if (section && educationalContent[section]) {
        return { data: { [section]: educationalContent[section] }, error: null };
      }

      return { data: educationalContent, error: null };
    } catch (error) {
      console.error(`❌ PublicContentService [${requestId}]: Error fetching educational content:`, error);
      return { data: null, error: this.handleError(error) };
    }
  }

  /**
   * Get FAQ content
   * 
   * @param {string} language - Language for content (bg/en)
   * @param {string} category - FAQ category to filter (optional)
   * @returns {Promise<{data: Array|null, error: string|null}>}
   */
  static async getFAQContent(language = LANGUAGES.BULGARIAN, category = null) {
    const cacheKey = 'faqContent';
    const requestId = `faq-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Check cache first
      if (this.isCacheValid(cacheKey)) {
        console.log(`📦 PublicContentService [${requestId}]: Using cached FAQ content`);
        const cachedData = this.cache[cacheKey].data;
        if (category) {
          const filtered = cachedData.filter(item => item.category === category);
          return { data: filtered, error: null };
        }
        return { data: cachedData, error: null };
      }

      // For now, FAQ content is static
      // In the future, this could fetch from an API
      const faqContent = language === LANGUAGES.BULGARIAN ? [
        {
          id: 1,
          category: 'general',
          question: 'Какво е ArbiWin.bg?',
          answer: 'ArbiWin.bg е платформа за анализ на арбитражни възможности в спортните залози.'
        },
        {
          id: 2,
          category: 'subscription',
          question: 'Как да се абонирам?',
          answer: 'Можете да се абонирате след регистрация в платформата.'
        },
        {
          id: 3,
          category: 'technical',
          question: 'Как често се обновяват данните?',
          answer: 'Данните се обновяват автоматично всеки 10 минути.'
        }
      ] : [
        {
          id: 1,
          category: 'general',
          question: 'What is ArbiWin.bg?',
          answer: 'ArbiWin.bg is a platform for analyzing arbitrage opportunities in sports betting.'
        },
        {
          id: 2,
          category: 'subscription',
          question: 'How do I subscribe?',
          answer: 'You can subscribe after registering on the platform.'
        },
        {
          id: 3,
          category: 'technical',
          question: 'How often is the data updated?',
          answer: 'Data is automatically updated every 10 minutes.'
        }
      ];

      // Update cache
      this.cache[cacheKey] = {
        data: faqContent,
        timestamp: Date.now(),
        ttl: 3600000
      };

      // Track analytics
      this.trackAnalytics('faq_content_view', { language, category });

      if (category) {
        const filtered = faqContent.filter(item => item.category === category);
        return { data: filtered, error: null };
      }

      return { data: faqContent, error: null };
    } catch (error) {
      console.error(`❌ PublicContentService [${requestId}]: Error fetching FAQ content:`, error);
      return { data: null, error: this.handleError(error) };
    }
  }

  /**
   * Submit contact form
   * 
   * @param {Object} formData - Contact form data
   * @param {string} formData.name - User name
   * @param {string} formData.email - User email
   * @param {string} formData.subject - Message subject
   * @param {string} formData.message - Message content
   * @param {string} language - Language for error messages (bg/en)
   * @returns {Promise<{success: boolean, error: string|null, data: Object|null}>}
   */
  static async submitContactForm(formData, language = LANGUAGES.BULGARIAN) {
    const endpoint = '/api/public/contact';
    const requestId = `contact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      console.log(`📧 PublicContentService [${requestId}]: Submitting contact form`);

      // Validate form data
      const validationError = this.validateContactForm(formData, language);
      if (validationError) {
        return { success: false, error: validationError, data: null };
      }

      // Submit to API
      const response = await apiClient.post(endpoint, {
        ...formData,
        language,
        timestamp: new Date().toISOString()
      }, {
        timeout: API_TIMEOUTS.DEFAULT
      });

      // Track analytics
      this.trackAnalytics('contact_form_submit', { 
        subject: formData.subject,
        success: true 
      });

      return { 
        success: true, 
        error: null, 
        data: response.data || response 
      };
    } catch (error) {
      console.error(`❌ PublicContentService [${requestId}]: Error submitting contact form:`, error);
      
      // Track analytics
      this.trackAnalytics('contact_form_submit', { 
        subject: formData?.subject,
        success: false,
        error: error.message
      });

      return { 
        success: false, 
        error: this.handleError(error, language), 
        data: null 
      };
    }
  }

  /**
   * Validate contact form data
   * 
   * @param {Object} formData - Form data to validate
   * @param {string} language - Language for error messages
   * @returns {string|null} Error message or null if valid
   */
  static validateContactForm(formData, language = LANGUAGES.BULGARIAN) {
    if (!formData.name || formData.name.trim() === '') {
      return language === LANGUAGES.BULGARIAN 
        ? 'Името е задължително'
        : 'Name is required';
    }

    if (!formData.email || formData.email.trim() === '') {
      return language === LANGUAGES.BULGARIAN
        ? 'Имейлът е задължителен'
        : 'Email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return language === LANGUAGES.BULGARIAN
        ? 'Моля въведете валиден имейл адрес'
        : 'Please enter a valid email address';
    }

    if (!formData.subject || formData.subject.trim() === '') {
      return language === LANGUAGES.BULGARIAN
        ? 'Темата е задължителна'
        : 'Subject is required';
    }

    if (!formData.message || formData.message.trim() === '') {
      return language === LANGUAGES.BULGARIAN
        ? 'Съобщението е задължително'
        : 'Message is required';
    }

    if (formData.message.trim().length < 10) {
      return language === LANGUAGES.BULGARIAN
        ? 'Съобщението трябва да бъде поне 10 символа'
        : 'Message must be at least 10 characters';
    }

    return null;
  }

  /**
   * Track analytics event
   * 
   * @param {string} eventType - Type of event (page_view, interaction, etc.)
   * @param {Object} eventData - Additional event data
   */
  static trackAnalytics(eventType, eventData = {}) {
    try {
      const event = {
        type: eventType,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        path: window.location.pathname,
        ...eventData
      };

      // Store in memory (in production, this would be sent to analytics service)
      this.analytics.interactions.push(event);

      // Keep only last 100 interactions in memory
      if (this.analytics.interactions.length > 100) {
        this.analytics.interactions.shift();
      }

      // Track page views
      if (eventType === 'page_view') {
        const page = eventData.page || window.location.pathname;
        const currentCount = this.analytics.pageViews.get(page) || 0;
        this.analytics.pageViews.set(page, currentCount + 1);
      }

      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`📊 Analytics [${eventType}]:`, event);
      }

      // In production, send to analytics API
      // await this.sendAnalyticsToAPI(event);
    } catch (error) {
      console.error('Error tracking analytics:', error);
    }
  }

  /**
   * Track page view
   * 
   * @param {string} page - Page path or identifier
   * @param {Object} metadata - Additional page metadata
   */
  static trackPageView(page, metadata = {}) {
    this.trackAnalytics('page_view', {
      page,
      ...metadata
    });
  }

  /**
   * Track user interaction
   * 
   * @param {string} interactionType - Type of interaction (click, scroll, etc.)
   * @param {string} element - Element identifier
   * @param {Object} metadata - Additional interaction metadata
   */
  static trackInteraction(interactionType, element, metadata = {}) {
    this.trackAnalytics('interaction', {
      interactionType,
      element,
      ...metadata
    });
  }

  /**
   * Get analytics data
   * 
   * @returns {Object} Analytics data
   */
  static getAnalytics() {
    return {
      pageViews: Object.fromEntries(this.analytics.pageViews),
      interactions: this.analytics.interactions.slice(-50), // Last 50 interactions
      sessionStart: this.analytics.sessionStart || new Date().toISOString()
    };
  }

  /**
   * Clear analytics data
   */
  static clearAnalytics() {
    this.analytics = {
      pageViews: new Map(),
      interactions: [],
      sessionStart: new Date().toISOString()
    };
  }

  /**
   * Get localized content
   * 
   * @param {string} contentKey - Content key to retrieve
   * @param {string} language - Language (bg/en)
   * @param {Object} params - Parameters for content interpolation
   * @returns {string} Localized content
   */
  static getLocalizedContent(contentKey, language = LANGUAGES.BULGARIAN, params = {}) {
    // This would typically fetch from a content API or use translation files
    // For now, return a placeholder
    const content = {
      [LANGUAGES.BULGARIAN]: {
        welcome: 'Добре дошли в ArbiWin.bg',
        description: 'Платформа за арбитражни залози'
      },
      [LANGUAGES.ENGLISH]: {
        welcome: 'Welcome to ArbiWin.bg',
        description: 'Arbitrage betting platform'
      }
    };

    let text = content[language]?.[contentKey] || contentKey;

    // Interpolate parameters
    Object.keys(params).forEach(key => {
      text = text.replace(`{${key}}`, params[key]);
    });

    return text;
  }

  /**
   * Handle API errors
   * 
   * @param {Error} error - Error object
   * @param {string} language - Language for error messages
   * @returns {string} User-friendly error message
   */
  static handleError(error, language = LANGUAGES.BULGARIAN) {
    if (!error) {
      return language === LANGUAGES.BULGARIAN
        ? 'Възникна неизвестна грешка'
        : 'An unknown error occurred';
    }

    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.message;

      switch (status) {
        case 400:
          return language === LANGUAGES.BULGARIAN
            ? 'Невалидна заявка. Моля проверете данните.'
            : 'Invalid request. Please check the data.';
        case 401:
          return language === LANGUAGES.BULGARIAN
            ? 'Необходима е автентификация.'
            : 'Authentication required.';
        case 403:
          return language === LANGUAGES.BULGARIAN
            ? 'Нямате права за достъп.'
            : 'Access denied.';
        case 404:
          return language === LANGUAGES.BULGARIAN
            ? 'Ресурсът не е намерен.'
            : 'Resource not found.';
        case 429:
          return language === LANGUAGES.BULGARIAN
            ? 'Твърде много заявки. Моля опитайте по-късно.'
            : 'Too many requests. Please try again later.';
        case 500:
        case 502:
        case 503:
          return language === LANGUAGES.BULGARIAN
            ? 'Сървърна грешка. Моля опитайте по-късно.'
            : 'Server error. Please try again later.';
        default:
          return message || (language === LANGUAGES.BULGARIAN
            ? 'Възникна грешка при комуникация със сървъра.'
            : 'An error occurred while communicating with the server.');
      }
    }

    if (error.request) {
      return language === LANGUAGES.BULGARIAN
        ? 'Проблем с връзката. Проверете интернет връзката си.'
        : 'Connection problem. Please check your internet connection.';
    }

    return error.message || (language === LANGUAGES.BULGARIAN
      ? 'Възникна неизвестна грешка'
      : 'An unknown error occurred');
  }

  /**
   * Clear cache
   * 
   * @param {string} cacheKey - Specific cache key to clear (optional)
   */
  static clearCache(cacheKey = null) {
    if (cacheKey) {
      this.cache[cacheKey] = {
        data: null,
        timestamp: null,
        ttl: 0
      };
    } else {
      // Clear all caches
      Object.keys(this.cache).forEach(key => {
        this.cache[key] = {
          data: null,
          timestamp: null,
          ttl: 0
        };
      });
    }
  }
}

export default PublicContentService;

